import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Flex from "antd/es/flex";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { useAccountSettings } from "../../hooks/api/admin/useAccountSettings";
import { Spinner } from "../shared/Spinner";
import { DataContext } from "../../context/DataContext";
import { AccountSettingsWrapper, SettingsGrid, SettingsWrapper } from "./style";
import {
  TEXT_COLORS,
  COLORS,
  BACKGROUND_COLORS,
  BORDER_COLORS,
} from "../../constants/design/colors";
import { FONT_SIZE, FONT_STYLE } from "../../constants/design/fontSize";
import { SettingsTable } from "./components/SettingsTable";
import { Select } from "../shared/Select";
import { SPACING } from "../../constants/design/spacing";
import { IAssetConfigs, CONFIG_TITLES } from "../../interfaces/settings";
import ConfirmationModal from "../shared/ConfirmationModal";
import { MarketDropdown } from "../Dropdown/MarketDropdown";
import { FilterWrapper } from "../Table/style";
import { ValidatorKeyEnum } from "../../enums/validate";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";

// Modal-specific form styles
const ModalFormRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${FONT_SIZE.oneHalf};
  margin-bottom: ${SPACING.three}px;

  > span:first-child {
    color: ${TEXT_COLORS.three};
    ${FONT_STYLE.body.three};
  }
`;

const ModalFormInput = styled.input.attrs({
  step: 0.000001,
})<{ hasErrors?: boolean }>`
  width: 150px;
  background-color: ${BACKGROUND_COLORS.four};
  border-radius: 4px;
  height: 32px;
  border: 1px solid
    ${({ hasErrors }) => (hasErrors ? COLORS.red.one : BORDER_COLORS.one)};
  color: ${TEXT_COLORS.one};
  padding: 0 ${SPACING.two}px;
  font-size: ${FONT_SIZE.oneHalf};
  text-align: left;

  &:focus {
    outline: none;
    border-color: ${({ hasErrors }) =>
      hasErrors ? COLORS.red.one : COLORS.blue.one};
  }
`;

const ModalFormError = styled.div`
  color: ${COLORS.red.one};
  font-size: ${FONT_SIZE.xs};
  text-align: left;
  margin-top: ${SPACING.one}px;
`;

const ModalFormWrapper = styled.div`
  margin-bottom: ${SPACING.four}px;
`;

const ModalInfoText = styled.div`
  ${FONT_STYLE.body.three};
  color: ${TEXT_COLORS.two};
  margin-top: ${SPACING.three}px;
`;

export function AccountSettings() {
  const { account } = useParams<{ account: string }>();
  const { setAddress, address } = useContext(DataContext);
  const {
    data: settingsData,
    isValidating,
    updateSettings,
    mutate,
  } = useAccountSettings();

  const [selectedInstrumentType, setInstrumentType] = useState<
    "OPTION" | "PERPETUAL" | "SPOT" | undefined
  >(undefined);
  const [selectedAsset, setAsset] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showModal, setShow] = useState<boolean>(false);
  const [payload, setPayload] = useState<Partial<IAssetConfigs>>();
  const [showSetAllModal, setShowSetAllModal] = useState<boolean>(false);
  const [isSetAllLoading, setIsSetAllLoading] = useState<boolean>(false);
  const [modalInstrumentType, setModalInstrumentType] = useState<
    "OPTION" | "PERPETUAL" | "SPOT" | undefined
  >(undefined);

  // Form for Set All modal
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      taker_fee: "",
      maker_fee: "",
    },
  });

  useEffect(() => {
    if (account) setAddress(account);
  }, [account, setAddress]);

  const filteredSettings = useMemo(() => {
    if (settingsData) {
      let filteredData = settingsData;

      if (selectedAsset) {
        filteredData = filteredData.filter(
          (setting) => setting.asset === selectedAsset
        );
      }

      if (selectedInstrumentType) {
        filteredData = filteredData.filter(
          (setting) => setting.instrument_type === selectedInstrumentType
        );
      }

      return filteredData;
    }
  }, [selectedAsset, selectedInstrumentType, settingsData]);

  // Get assets for each instrument type
  const assetsForOptions = useMemo(() => {
    if (settingsData) {
      const assets = new Set<string>();
      settingsData.forEach((config) => {
        if (config.instrument_type === "OPTION") {
          assets.add(config.asset);
        }
      });
      return Array.from(assets);
    }
    return [];
  }, [settingsData]);

  const assetsForPerpetuals = useMemo(() => {
    if (settingsData) {
      const assets = new Set<string>();
      settingsData.forEach((config) => {
        if (config.instrument_type === "PERPETUAL") {
          assets.add(config.asset);
        }
      });
      return Array.from(assets);
    }
    return [];
  }, [settingsData]);

  const assetsForSpot = useMemo(() => {
    if (settingsData) {
      const assets = new Set<string>();
      settingsData.forEach((config) => {
        if (config.instrument_type === "SPOT") {
          assets.add(config.asset);
        }
      });
      return Array.from(assets);
    }
    return [];
  }, [settingsData]);

  // Get first asset's fees for default values based on modal instrument type
  const firstAssetFees = useMemo(() => {
    if (settingsData && modalInstrumentType) {
      const firstConfig = settingsData.find(
        (config) => config.instrument_type === modalInstrumentType
      );
      if (firstConfig) {
        return {
          taker_fee: firstConfig.taker_fee || "",
          maker_fee: firstConfig.maker_fee || "",
        };
      }
    }
    return { taker_fee: "", maker_fee: "" };
  }, [settingsData, modalInstrumentType]);

  // Get assets for the modal instrument type
  const assetsForModalInstrumentType = useMemo(() => {
    if (modalInstrumentType === "OPTION") return assetsForOptions;
    if (modalInstrumentType === "PERPETUAL") return assetsForPerpetuals;
    if (modalInstrumentType === "SPOT") return assetsForSpot;
    return [];
  }, [
    modalInstrumentType,
    assetsForOptions,
    assetsForPerpetuals,
    assetsForSpot,
  ]);

  // Update form defaults when modal opens
  useEffect(() => {
    if (showSetAllModal && modalInstrumentType) {
      reset({
        taker_fee: firstAssetFees.taker_fee,
        maker_fee: firstAssetFees.maker_fee,
      });
    }
  }, [showSetAllModal, modalInstrumentType, firstAssetFees, reset]);

  const assetFilter = useMemo(() => {
    // Loop through configs to retrieve filterable assets
    if (settingsData) {
      const assets = new Set<string>();

      settingsData.forEach((config) => {
        if (!assets.has(config.asset)) {
          assets.add(config.asset);
        }
      });

      const options = [
        {
          label: "All Assets",
          value: "",
        },
      ];

      Array.from(assets).forEach((asset) =>
        options.push({
          label: asset,
          value: asset,
        })
      );

      return <MarketDropdown onClick={setAsset} />;
    }
  }, [settingsData]);

  const onUpdate = useCallback(async () => {
    if (payload) {
      setLoading(true);
      await updateSettings(payload).finally(() => {
        mutate();
        setLoading(false);
      });
    }
  }, [mutate, payload, updateSettings]);

  // Handle Set All submission
  const onSetAll = useCallback(
    async (formData: { taker_fee: string; maker_fee: string }) => {
      if (
        !modalInstrumentType ||
        !address ||
        assetsForModalInstrumentType.length === 0
      ) {
        return;
      }

      setIsSetAllLoading(true);
      try {
        // Loop through all assets for the modal instrument type
        const updatePromises = assetsForModalInstrumentType.map((asset) =>
          updateSettings({
            account: address,
            asset,
            instrument_type: modalInstrumentType,
            taker_fee: formData.taker_fee,
            maker_fee: formData.maker_fee,
          })
        );

        await Promise.all(updatePromises);
        await mutate();
        setShowSetAllModal(false);
        reset();
        setModalInstrumentType(undefined);
      } catch (error) {
        // Error handling is done in updateSettings
      } finally {
        setIsSetAllLoading(false);
      }
    },
    [
      modalInstrumentType,
      address,
      assetsForModalInstrumentType,
      updateSettings,
      mutate,
      reset,
    ]
  );

  const errorContent = useCallback(
    (field: "taker_fee" | "maker_fee") => {
      const error = errors?.[field] as any;
      if (!error) return null;

      if (error.type === ValidatorKeyEnum.GteZero) {
        return (
          <ModalFormError>
            {CONFIG_TITLES[field]} must be greater than / equal to 0
          </ModalFormError>
        );
      }
      if (error.type === ValidatorKeyEnum.LteOne) {
        return (
          <ModalFormError>
            {CONFIG_TITLES[field]} must be lesser or equal to 1
          </ModalFormError>
        );
      }
      if (error.type === ValidatorKeyEnum.GtNegOne) {
        return (
          <ModalFormError>
            {CONFIG_TITLES[field]} must be greater than -1
          </ModalFormError>
        );
      }
      return null;
    },
    [errors]
  );

  const getInstrumentTypeLabel = (
    instrumentType?: "OPTION" | "PERPETUAL" | "SPOT"
  ) => {
    const type = instrumentType || modalInstrumentType;
    if (type === "OPTION") return "Options";
    if (type === "PERPETUAL") return "Perpetuals";
    if (type === "SPOT") return "Spot";
    return "";
  };

  return (
    <>
      <ConfirmationModal
        primaryColor={COLORS.blue.one}
        secondaryColor={COLORS.blue.two}
        tertiaryColor={COLORS.blue.three}
        title={"Edit Asset Configurations"}
        show={showModal}
        minWidth={"250px"}
        onHide={() => setShow(false)}
        stats={
          payload
            ? [
                {
                  title: "",
                  data: (
                    <div style={{ marginBottom: SPACING.three }}>
                      Here are the changes you are making for {payload.asset}{" "}
                      {payload.instrument_type}:
                    </div>
                  ),
                },
                ...Object.keys(payload)
                  .filter(
                    (key) =>
                      !["account", "asset", "instrument_type"].includes(key)
                  )
                  .map((key) => ({
                    title: <div style={{ color: TEXT_COLORS.one }}>{key}</div>,
                    data: payload[key as keyof IAssetConfigs]!,
                  })),
              ]
            : []
        }
        confirmationButton={{
          title: "Update Settings",
          onClick: onUpdate,
          disabled: isLoading,
        }}
      />

      {/* Set All Modal */}
      <ConfirmationModal
        primaryColor={COLORS.blue.one}
        secondaryColor={COLORS.blue.two}
        tertiaryColor={COLORS.blue.three}
        title={`Set All ${getInstrumentTypeLabel()} Taker and Maker Fee`}
        show={showSetAllModal}
        minWidth={"400px"}
        onHide={() => {
          setShowSetAllModal(false);
          reset();
          setModalInstrumentType(undefined);
        }}
        extras={
          <form onSubmit={handleSubmit(onSetAll)}>
            <ModalFormWrapper>
              <ModalFormRow>
                <span>{CONFIG_TITLES.taker_fee}</span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <ModalFormInput
                    hasErrors={Object.keys(errors).includes("taker_fee")}
                    type="number"
                    {...register("taker_fee", {
                      required: "Taker fee is required",
                      validate: {
                        [ValidatorKeyEnum.GteZero]: (v) => Number(v) >= 0,
                        [ValidatorKeyEnum.LteOne]: (v) => Number(v) <= 1,
                      },
                    })}
                    onChange={(e) => {
                      setValue("taker_fee", String(e.target.value));
                      trigger("taker_fee");
                    }}
                  />
                  {errorContent("taker_fee")}
                </div>
              </ModalFormRow>
              <ModalFormRow>
                <span>{CONFIG_TITLES.maker_fee}</span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <ModalFormInput
                    hasErrors={Object.keys(errors).includes("maker_fee")}
                    type="number"
                    {...register("maker_fee", {
                      required: "Maker fee is required",
                      validate: {
                        [ValidatorKeyEnum.GtNegOne]: (v) => Number(v) > -1,
                        [ValidatorKeyEnum.LteOne]: (v) => Number(v) <= 1,
                      },
                    })}
                    onChange={(e) => {
                      setValue("maker_fee", String(e.target.value));
                      trigger("maker_fee");
                    }}
                  />
                  {errorContent("maker_fee")}
                </div>
              </ModalFormRow>
              <ModalInfoText>
                This will update {assetsForModalInstrumentType.length} asset
                {assetsForModalInstrumentType.length !== 1 ? "s" : ""} for{" "}
                {getInstrumentTypeLabel()}
              </ModalInfoText>
            </ModalFormWrapper>
          </form>
        }
        confirmationButton={{
          title: "Set All Fees",
          onClick: handleSubmit(onSetAll),
          disabled: isSetAllLoading || Object.keys(errors).length > 0,
        }}
        isLoading={isSetAllLoading}
      />

      <AccountSettingsWrapper>
        <SettingsWrapper isAccount>
          <Flex
            gap={SPACING.three}
            wrap
            align="center"
            style={{ marginBottom: SPACING.two }}
          >
            <div>
              <FilterWrapper>Assets</FilterWrapper>
              {assetFilter}
            </div>
            <div>
              <FilterWrapper>Market Type</FilterWrapper>
              <Select
                theme="blue"
                options={[
                  {
                    label: "All Markets",
                    isActive: !selectedInstrumentType,
                    onClick: () => setInstrumentType(undefined),
                  },
                  {
                    label: "Options",
                    isActive: selectedInstrumentType === "OPTION",
                    onClick: () => setInstrumentType("OPTION"),
                  },
                  {
                    label: "Perpetuals",
                    isActive: selectedInstrumentType === "PERPETUAL",
                    onClick: () => setInstrumentType("PERPETUAL"),
                  },
                  {
                    label: "Spot",
                    isActive: selectedInstrumentType === "SPOT",
                    onClick: () => setInstrumentType("SPOT"),
                  },
                ]}
              />
            </div>
            <div
              style={{ display: "flex", gap: SPACING.two, flexWrap: "wrap" }}
            >
              {assetsForOptions.length > 0 && (
                <Button
                  buttonTheme={ButtonThemeEnum.NEUTRAL2}
                  onClick={() => {
                    setModalInstrumentType("OPTION");
                    setShowSetAllModal(true);
                  }}
                  style={{ marginTop: "20px" }}
                >
                  Set All Options Taker and Maker Fee
                </Button>
              )}
              {assetsForPerpetuals.length > 0 && (
                <Button
                  buttonTheme={ButtonThemeEnum.NEUTRAL2}
                  onClick={() => {
                    setModalInstrumentType("PERPETUAL");
                    setShowSetAllModal(true);
                  }}
                  style={{ marginTop: "20px" }}
                >
                  Set All Perpetuals Taker and Maker Fee
                </Button>
              )}
              {assetsForSpot.length > 0 && (
                <Button
                  buttonTheme={ButtonThemeEnum.NEUTRAL2}
                  onClick={() => {
                    setModalInstrumentType("SPOT");
                    setShowSetAllModal(true);
                  }}
                  style={{ marginTop: "20px" }}
                >
                  Set All Spot Taker and Maker Fee
                </Button>
              )}
            </div>
          </Flex>
          {
            // eslint-disable-next-line no-nested-ternary
            !filteredSettings ? (
              isValidating ? (
                <Spinner />
              ) : (
                <div>No settings data for {account}</div>
              )
            ) : (
              <SettingsGrid>
                <AnimatePresence>
                  {filteredSettings.map((setting, i) => (
                    <SettingsTable
                      key={i}
                      setting={setting}
                      updateSettings={(p) => {
                        setShow(true);
                        setPayload(p);
                      }}
                    />
                  ))}
                </AnimatePresence>
              </SettingsGrid>
            )
          }
        </SettingsWrapper>
      </AccountSettingsWrapper>
    </>
  );
}
